import React, { useState, ReactElement, useRef, useEffect } from 'react';

import { NavigationItem } from '@pb/shared/ui/atoms/navigation-item';
import { Container } from '@pb/shared/ui/atoms/container';
import {
    TAccountNavigationMenuProperties,
    TAccountNavigationMenuItem,
} from './account-navigation-menu.types';
import { StyledAccountNavigationMenu } from './account-navigation-menu.styled';
import { handleClickedOutsideMainMenu } from './account-navigation-menu.logic';

function RenderMenuItems({
    label,
    icon,
    href,
    children,
    isExpanded,
    onClick,
}: TAccountNavigationMenuItem): ReactElement {
    if (!children) {
        return (
            <NavigationItem
                key={label}
                label={label}
                icon={icon}
                href={href}
                isExpanded={isExpanded}
                onClick={onClick}
            />
        );
    }

    return (
        <NavigationItem
            key={label}
            label={label}
            icon={icon}
            href={href}
            isExpanded={isExpanded}
            onClick={onClick}
        >
            {children.map(child => RenderMenuItems({ ...child }))}
        </NavigationItem>
    );
}

export function AccountNavigationMenu({
    navigationMenuItems,
    isMenuOpen,
}: TAccountNavigationMenuProperties): ReactElement | null {
    const accountNavigationReference = useRef<HTMLUListElement>(null);

    const [expandedNavigationItem, setExpandedNavigationItem] =
        useState<string>();

    const handleClickedOutsideMainMenuCurry = handleClickedOutsideMainMenu(
        accountNavigationReference,
        setExpandedNavigationItem
    );

    function handleNavigationItemClick(label: string): void {
        setExpandedNavigationItem(previousState =>
            previousState === label ? undefined : label
        );
    }

    useEffect(() => {
        if (expandedNavigationItem) {
            document.addEventListener(
                'mousedown',
                handleClickedOutsideMainMenuCurry
            );
        } else {
            document.removeEventListener(
                'mousedown',
                handleClickedOutsideMainMenuCurry
            );
        }

        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickedOutsideMainMenuCurry
            );
        };
    }, [expandedNavigationItem]);

    return (
        <Container as="nav" fullWidth aria-label="Main Menu">
            <StyledAccountNavigationMenu
                ref={accountNavigationReference}
                isMenuOpen={isMenuOpen}
                data-testid="styled-account-navigation-menu"
            >
                {navigationMenuItems.map(navigationMenuItem => (
                    <RenderMenuItems
                        key={navigationMenuItem.label}
                        {...navigationMenuItem}
                        isExpanded={
                            navigationMenuItem.label === expandedNavigationItem
                        }
                        onClick={handleNavigationItemClick}
                    />
                ))}
            </StyledAccountNavigationMenu>
        </Container>
    );
}
